import { fromJS, isList, Set, List, Map, OrderedMap, Record } from 'immutable';

import { HotkeyFactory, ParamsFactory, UploadQueueItemFactory } from '@/domain/documents/factories';
import moment from 'moment';
import { documentCompositionFunctionsAdapter } from '@/domain/documents/helpers';
import { GRID_SERVICE_KEYS } from '@/components/AgGrid/helpers';

import { WS_GRID_PRESET_TYPES } from '@/domain/documents/constants';

export const paramsAdapter = (data) => {
  const { creationDate: creation, ...rest } = data || {};

  const creationDate =
    creation && !moment(creation).isSame(moment(), 'day') ? creation.format('YYYY-MM-DD[T]HH:mm:ss') : null;
  return ParamsFactory({ creationDate, ...rest });
};

export const uploadQueueItemAdapter = ({ params, ...rest }) =>
  UploadQueueItemFactory({
    params: paramsAdapter(params),
    ...rest,
  });

const GridHeaderItemFactory = new Record({
  field: '',
  headerName: '',
  type: 'string',
  hide: false,
  pinned: false,
  minWidth: 50,
  operator: undefined,
  filterParams: undefined,
  filter: undefined,
  sortable: true,
});

const fieldsByTypes = {
  extraSelect: '.status',
};

const getField = (item) => {
  const endOfField = fieldsByTypes[item.type];
  return `${item.field}${endOfField || ''}`;
};

const GridHeaderItemAdapter = (item) =>
  GridHeaderItemFactory({
    ...item,
    field: getField(item),
    minWidth: +item.minWidth || undefined,
  });

const createGridMetaItem = (item) => {
  // eslint-disable-next-line camelcase
  const { document, next_approver, ...rest } = item;
  // eslint-disable-next-line camelcase
  const nextApproverModified = typeof next_approver === 'string' ? [next_approver] : next_approver;

  return Map({
    documentID: document.document_id,
    next_approver: nextApproverModified,
    [GRID_SERVICE_KEYS.CONTEXT_MENU_COLUMN_NAME]: '',
    [GRID_SERVICE_KEYS.PREVIEW_CHECKBOX_COLUMN_NAME]: '',
    [GRID_SERVICE_KEYS.COLUMNS_VISIBILITY_MENU]: '',
    ...rest,
  });
};
// data stored in state
export const gridDocumentsListAdapter = (rawList) =>
  rawList.reduce((a, v) => {
    // eslint-disable-next-line camelcase
    const { document_id, notes, ...rest } = v.document;

    return a.set(
      document_id,
      documentCompositionFunctionsAdapter({
        ...rest,
        notes: notes || '',
        // eslint-disable-next-line camelcase
        documentID: document_id,
        gridMeta: createGridMetaItem(v),
      }),
    );
  }, new OrderedMap());

export const gridHeadersListAdapter = (rawList) => List(rawList.map((item) => GridHeaderItemAdapter(item)));

// data that relevant for ag-grid
export const documentsGridRowNodesAdapter = (adaptedList) => {
  if (isList(adaptedList)) {
    return adaptedList.map((documentID) => ({ documentID })).toArray();
  }

  return adaptedList.reduce(
    (a, v) => [
      ...a,
      {
        rowDocumentData: v,
        ...v.gridMeta.toJS(),
      },
    ],
    [],
  );
};

// eslint-disable-next-line max-len
const documentHotkeyAdapter = (item) =>
  HotkeyFactory({
    keys: fromJS(item.keys),
    description: item.description,
    level: item.level,
  });

export const documentHotkeysAdapter = (rawList) => List(rawList.map((item) => documentHotkeyAdapter(item)));

export const documentCurrentViewersAdapter = Set;

export function hotkeyMatchAdapter(hotkeyMatch) {
  return hotkeyMatch ? { hotkey: hotkeyMatch } : {};
}

const WS_GRID_PRESET_TYPE_VALUES = Object.values(WS_GRID_PRESET_TYPES);

export const wsGridPresetAdapter = ({
  company_id: companyId = null,
  user_id: userId = null,
  erp_type: erpType = null,
  type = null,
  ...rest
}) => ({
  ...rest,
  companyId,
  userId,
  erpType,
  isAll: !companyId,
  type: WS_GRID_PRESET_TYPE_VALUES.includes(type) ? type : WS_GRID_PRESET_TYPES.custom,
});

export const wsGridPresetsAdapter = (presets) => presets.map((p) => wsGridPresetAdapter(p));

export const wsGridSharePresetAdapter = (preset) => ({
  ...preset,
  companyId: preset.company_id || null,
  userId: preset.user_id || null,
  erpType: preset.erp_type || null,
  isAll: !preset.company_id,
  name: 'Shared',
  type: WS_GRID_PRESET_TYPES.shared,
});
